import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../../../utils/seo';
import Icon from '../../../components/base/Icon';

const QUOTE_FORM_URL = 'https://readdy.ai/api/form/d6pee1c4k19g20dvqppg';

const SERVICE_OPTIONS = [
  { value: '', label: 'Select a service...' },
  { value: 'Handyman Services', label: 'Handyman Services' },
  { value: 'Fence Installation & Repair', label: 'Fence Installation & Repair' },
  { value: 'Bathroom Remodels', label: 'Bathroom Remodels' },
  { value: 'Small Remodel Projects', label: 'Small Remodel Projects' },
  { value: 'Other', label: 'Other' },
];

interface FormState {
  full_name: string;
  phone: string;
  email: string;
  service_needed: string;
  project_details: string;
}

const emptyForm = (): FormState => ({
  full_name: '',
  phone: '',
  email: '',
  service_needed: '',
  project_details: '',
});

function QuoteForm() {
  const [values, setValues] = useState<FormState>(emptyForm());
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setValues((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const validate = (): string => {
    if (!values.full_name.trim()) return 'Full name is required.';
    if (!values.phone.trim()) return 'Phone number is required.';
    if (!values.email.trim()) return 'Email address is required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) return 'Please enter a valid email address.';
    if (!values.service_needed) return 'Please select a service.';
    if (!values.project_details.trim()) return 'Please describe your project.';
    return '';
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setErrorMsg(validationError);
      setStatus('error');
      return;
    }
    setStatus('submitting');
    setErrorMsg('');
    try {
      const params = new URLSearchParams();
      (Object.keys(values) as (keyof FormState)[]).forEach((key) => {
        params.append(key, values[key]);
      });
      const res = await fetch(QUOTE_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (!res.ok) throw new Error('Submission failed');
      setStatus('success');
      setValues(emptyForm());
    } catch {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again or call us at (208) 204-6331.');
    }
  };

  const inputClass = 'w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B45309] focus:border-transparent text-sm';

  return (
    <form onSubmit={handleSubmit} data-readdy-form id="handyman-faq-quote-form" noValidate>
      {status === 'success' && (
        <div className="mb-5 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm flex items-start gap-2" role="alert">
          <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><Icon name="check" className="w-4 h-4" /></span>
          <span>Thanks — we received your request and will be in touch within 24 hours.</span>
        </div>
      )}
      {status === 'error' && errorMsg && (
        <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start gap-2" role="alert">
          <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><Icon name="circle-exclamation" className="w-4 h-4" /></span>
          <span>{errorMsg}</span>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor="hfq-full_name" className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
          <input type="text" id="hfq-full_name" name="full_name" required value={values.full_name} onChange={handleChange} className={inputClass} placeholder="John Smith" />
        </div>
        <div>
          <label htmlFor="hfq-phone" className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
          <input type="tel" id="hfq-phone" name="phone" required value={values.phone} onChange={handleChange} className={inputClass} placeholder="(208) 555-0123" />
        </div>
      </div>
      <div className="mb-4">
        <label htmlFor="hfq-email" className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
        <input type="email" id="hfq-email" name="email" required value={values.email} onChange={handleChange} className={inputClass} placeholder="john@example.com" />
      </div>
      <div className="mb-4">
        <label htmlFor="hfq-service_needed" className="block text-sm font-medium text-gray-700 mb-1">Service Needed *</label>
        <select id="hfq-service_needed" name="service_needed" required value={values.service_needed} onChange={handleChange} className={`${inputClass} cursor-pointer`}>
          {SERVICE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      <div className="mb-6">
        <label htmlFor="hfq-project_details" className="block text-sm font-medium text-gray-700 mb-1">Project Details *</label>
        <textarea id="hfq-project_details" name="project_details" required rows={3} maxLength={500} value={values.project_details} onChange={handleChange} className={`${inputClass} resize-none`} placeholder="Describe what you need help with..." />
        <p className="text-xs text-gray-500 mt-1">{values.project_details.length}/500 characters</p>
      </div>
      <button type="submit" disabled={status === 'submitting'} className="w-full bg-[#B45309] hover:bg-[#92400E] disabled:opacity-60 text-white px-6 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2">
        {status === 'submitting' ? 'Sending...' : (
          <>
            Request Free Estimate
            <span className="w-4 h-4 flex items-center justify-center"><Icon name="arrow-right" className="w-4 h-4" /></span>
          </>
        )}
      </button>
    </form>
  );
}

const faqs = [
  {
    category: 'Pricing & Minimums',
    icon: 'tag',
    items: [
      {
        q: 'Is there a minimum service call fee?',
        short: 'Yes — we have a minimum charge to cover travel and setup time.',
        detail: 'Our minimum service call is typically 1–2 hours of labor, depending on the job type and your location within the Treasure Valley. This covers the time it takes to drive to your home, set up, and complete even a very small task. If you have multiple small jobs, we recommend bundling them into a single visit to get the most value from the minimum.',
      },
      {
        q: 'What\'s included in the hourly rate?',
        short: 'Labor and standard hand tools are included. Materials are billed separately.',
        detail: 'Our hourly rate covers the technician\'s time, expertise, and all standard hand tools needed to complete the job. Specialty equipment rentals (like a tile saw or scaffolding) and all materials — lumber, hardware, caulk, paint, etc. — are billed at cost plus a small handling fee. We\'ll always give you a clear breakdown before we start.',
      },
      {
        q: 'Do you charge for estimates?',
        short: 'No — estimates are always free.',
        detail: 'We provide free written estimates for all projects. For straightforward handyman jobs, we can often give you a ballpark over the phone. For larger or more complex scopes, we\'ll schedule a brief on-site visit to assess the work before providing a written estimate. You\'re never obligated to proceed after receiving an estimate.',
      },
    ],
  },
  {
    category: 'Scheduling & Availability',
    icon: 'calendar-days',
    items: [
      {
        q: 'How far out are you typically booked?',
        short: 'Most handyman jobs can be scheduled within 3–7 business days.',
        detail: 'Our availability varies by season — spring and summer tend to be busier. For urgent repairs (a broken door lock, a leaking fixture, etc.), call us directly at (208) 204-6331 and we\'ll do our best to fit you in quickly. Larger projects like bathroom remodels or multi-day jobs are typically scheduled 1–3 weeks out.',
      },
      {
        q: 'Do you work on weekends?',
        short: 'We offer limited Saturday availability — call to check current openings.',
        detail: 'We work Monday through Friday as our primary schedule, with select Saturday slots available depending on current workload. Sunday appointments are not available. If you need a weekend appointment, call us early in the week to check availability and get on the schedule before slots fill up.',
      },
    ],
  },
  {
    category: 'Materials & Supplies',
    icon: 'box-open',
    items: [
      {
        q: 'Do you supply materials, or do I need to buy them?',
        short: 'We can supply materials, or you can purchase them yourself — your choice.',
        detail: 'For most jobs, we\'re happy to source and supply all necessary materials, which we bill at cost plus a small handling fee. If you prefer to purchase materials yourself to save money or match a specific product, that works too — just make sure everything is on-site before the scheduled start time. We\'ll give you a clear materials list in advance if needed.',
      },
      {
        q: 'What if additional materials are needed mid-job?',
        short: 'We\'ll notify you before purchasing anything beyond the original scope.',
        detail: 'If we discover additional materials are needed once work is underway — for example, hidden rot behind drywall or more fasteners than expected — we\'ll stop and communicate with you before proceeding. We never make unilateral purchasing decisions that affect your bill. You\'ll always approve any scope or cost changes before they happen.',
      },
    ],
  },
  {
    category: 'Safety & Licensing',
    icon: 'shield-halved',
    items: [
      {
        q: 'Are you licensed and insured in Idaho?',
        short: 'Yes — Fix Right Services is fully licensed and carries general liability insurance.',
        detail: 'We are compliant with Idaho contractor licensing requirements and carry general liability insurance to protect you and your property. We\'re happy to provide a Certificate of Insurance upon request before any work begins. Always ask any contractor you hire for proof of both — it\'s your right as a homeowner.',
      },
      {
        q: 'How do you handle safety on the job site?',
        short: 'We follow standard safety practices and keep your home clean and protected throughout the job.',
        detail: 'We use drop cloths to protect floors and furniture, wear appropriate PPE for the task at hand, and follow OSHA-aligned safety practices. We never leave tools or materials in unsafe positions, and we clean up thoroughly at the end of each workday. If a job requires working at height or with power tools near finished surfaces, we take extra precautions to protect your home.',
      },
    ],
  },
  {
    category: 'Electrical & Plumbing Scope',
    icon: 'bolt',
    items: [
      {
        q: 'What small electrical tasks can you handle?',
        short: 'We handle basic fixture swaps and device replacements — not panel work or new circuits.',
        detail: 'We can replace light fixtures, ceiling fans, outlets, switches, and similar like-for-like swaps that don\'t require new wiring or panel modifications. We do not perform electrical panel upgrades, new circuit installation, or any work that requires an electrical permit under Idaho code. For those tasks, you\'ll need a licensed electrician — see our "When You Need a Specialist" section below.',
      },
      {
        q: 'What small plumbing tasks can you handle?',
        short: 'We handle fixture replacements and minor repairs — not drain line work or water heater installs.',
        detail: 'We can replace faucets, toilets, showerheads, supply lines, shut-off valves, and similar fixtures. We can also address minor leaks at accessible connections. We do not perform drain line repairs, water heater installations, gas line work, or anything requiring a plumbing permit. Those jobs require a licensed plumber.',
      },
    ],
  },
  {
    category: 'Warranties & Workmanship',
    icon: 'certificate',
    items: [
      {
        q: 'Do you warranty your work?',
        short: 'Yes — we stand behind our workmanship.',
        detail: 'We warranty our labor for 30 days on standard handyman repairs. If something we installed or repaired fails due to our workmanship within that period, we\'ll come back and make it right at no charge. Material warranties are separate and governed by the manufacturer. For larger remodel projects, warranty terms are outlined in the written contract.',
      },
      {
        q: 'What if I\'m not satisfied with the work?',
        short: 'We do a final walkthrough with you before we consider any job complete.',
        detail: 'Before we pack up and leave, we walk through the completed work with you to make sure everything meets your expectations. If something isn\'t right, we address it on the spot. We don\'t consider a job done until you\'re satisfied. If an issue comes up after we leave, contact us directly — we\'re responsive and we take our reputation seriously.',
      },
    ],
  },
];

const specialists = [
  {
    icon: 'bolt',
    title: 'Licensed Electrician',
    when: 'Panel upgrades, new circuit installation, rewiring, EV charger installation, any work requiring an electrical permit.',
    why: 'Idaho requires a licensed electrician for permitted electrical work. Unpermitted electrical work can void homeowner\'s insurance and create serious fire hazards.',
  },
  {
    icon: 'droplet',
    title: 'Licensed Plumber',
    when: 'Drain line repairs or replacement, water heater installation, gas line work, sewer connections, any work requiring a plumbing permit.',
    why: 'Plumbing permits exist to protect your home\'s water supply and structural integrity. Licensed plumbers carry the required bond and insurance for this work.',
  },
  {
    icon: 'fire-flame-curved',
    title: 'HVAC Technician',
    when: 'Furnace or AC installation or repair, ductwork modifications, gas appliance connections, refrigerant handling.',
    why: 'HVAC work involves gas lines, refrigerants, and combustion systems. Idaho requires licensed HVAC contractors for this work — and for good reason.',
  },
  {
    icon: 'house-chimney-crack',
    title: 'Structural Engineer or General Contractor',
    when: 'Load-bearing wall removal, foundation work, structural repairs, additions, or any project requiring a building permit for structural changes.',
    why: 'Structural modifications affect the safety of your entire home. These projects require engineered drawings, permits, and inspections — not a handyman.',
  },
  {
    icon: 'tree',
    title: 'Certified Arborist',
    when: 'Large tree removal, trimming near power lines, root intrusion issues, or any tree work that could affect structures.',
    why: 'Tree work near structures or utilities is high-risk. A certified arborist carries the right insurance and equipment to do it safely.',
  },
  {
    icon: 'shield-virus',
    title: 'Licensed Remediation Contractor',
    when: 'Mold remediation, asbestos abatement, lead paint removal, or any hazardous material handling.',
    why: 'Disturbing mold, asbestos, or lead paint without proper containment and disposal can create serious health hazards for your family. These require licensed specialists.',
  },
];

export default function HandymanFAQPage() {
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  useSEO({
    title: 'Handyman FAQ — Fix Right Services Boise Idaho',
    description: 'Answers to the most common handyman questions: minimum service calls, what\'s included, scheduling, materials, small electrical & plumbing limits, warranties, and when to call a specialist. Serving Boise & Treasure Valley.',
    keywords: 'handyman FAQ Boise, handyman questions Idaho, Fix Right Services FAQ, handyman service call minimum, handyman warranty Boise',
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://fixrightservices.com'}/services/handyman-faq`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      'mainEntity': faqs.flatMap((cat) =>
        cat.items.map((faq) => ({
          '@type': 'Question',
          'name': faq.q,
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': `${faq.short} ${faq.detail}`,
          },
        }))
      ),
    },
  });

  const toggleFaq = (key: string) => {
    setOpenFaq((prev) => (prev === key ? null : key));
  };

  return (
    <div className="min-h-screen bg-white">

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm" aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-8">
              <Link to="/">
                <img
                  src="https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/2ea97a783d40b0afc97fadf5f7236f78.webp"
                  alt="Fix Right Services Logo"
                  width="180"
                  height="56"
                  className="h-14 w-auto"
                  loading="eager"
                />
              </Link>
              <div className="hidden md:flex items-center gap-6 text-sm font-medium text-gray-700">
                <Link to="/" className="hover:text-[#B45309] transition-colors cursor-pointer">Home</Link>
                <Link to="/services" className="text-[#B45309] font-semibold cursor-pointer">Services</Link>
                <Link to="/blog" className="hover:text-[#B45309] transition-colors cursor-pointer">Blog</Link>
              </div>
            </div>
            <a
              href="tel:+12082046331"
              className="flex items-center gap-2 bg-[#B45309] hover:bg-[#92400E] text-white px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              aria-label="Call Fix Right Services"
            >
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="phone" className="w-4 h-4" /></span>
              <span className="hidden sm:inline">(208) 204-6331</span>
              <span className="sm:hidden">Call Now</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden" aria-label="Page hero">
        <div className="absolute inset-0 z-0">
          <img
            src="https://public.readdy.ai/ai/img_res/afeaa06e404fc032b533bb92d225b0ce.jpg"
            alt="Fix Right Services Handyman FAQ"
            width="1920"
            height="600"
            className="w-full h-full object-cover object-top"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-black/55"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center w-full">
          <div className="inline-flex items-center gap-2 bg-[#B45309]/90 text-white text-sm font-medium px-4 py-2 rounded-full mb-6">
            <span className="w-4 h-4 flex items-center justify-center"><Icon name="circle-question" className="w-3.5 h-3.5" /></span>
            Handyman FAQ
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-5 leading-tight">
            Fix Right Services — Handyman Questions Answered
          </h1>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Everything Boise homeowners ask before booking — pricing, scheduling, materials, electrical &amp; plumbing limits, warranties, and more.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:+12082046331"
              className="inline-flex items-center gap-2 bg-[#B45309] hover:bg-[#92400E] text-white px-7 py-3.5 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="phone" className="w-4 h-4" /></span>
              Call (208) 204-6331
            </a>
            <a
              href="#quote"
              className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-900 px-7 py-3.5 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="file-lines" className="w-4 h-4" /></span>
              Get a Free Estimate
            </a>
          </div>
        </div>
      </section>

      {/* Breadcrumb + Quick Nav */}
      <section className="bg-gray-50 border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <nav aria-label="Breadcrumb" className="text-sm text-gray-500">
            <ol className="flex items-center gap-2">
              <li><Link to="/" className="hover:text-[#B45309] transition-colors cursor-pointer">Home</Link></li>
              <li><span className="w-3 h-3 flex items-center justify-center"><Icon name="chevron-right" className="w-3 h-3" /></span></li>
              <li><Link to="/services" className="hover:text-[#B45309] transition-colors cursor-pointer">Services</Link></li>
              <li><span className="w-3 h-3 flex items-center justify-center"><Icon name="chevron-right" className="w-3 h-3" /></span></li>
              <li className="text-gray-900 font-medium">Handyman FAQ</li>
            </ol>
          </nav>
          <div className="flex flex-wrap gap-2 text-xs">
            {faqs.map((cat) => (
              <a
                key={cat.category}
                href={`#cat-${cat.category.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
                className="inline-flex items-center gap-1.5 bg-white border border-gray-200 hover:border-[#B45309] hover:text-[#B45309] text-gray-600 px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center"><Icon name={cat.icon} className="w-3 h-3" /></span>
                {cat.category}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Sections */}
      <main className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">

          <div className="grid lg:grid-cols-3 gap-12">

            {/* FAQ List */}
            <div className="lg:col-span-2 space-y-14">
              {faqs.map((cat) => {
                const catSlug = cat.category.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                return (
                  <section key={cat.category} id={`cat-${catSlug}`} aria-labelledby={`heading-${catSlug}`}>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-[#B45309]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon name={cat.icon} className="w-5 h-5 text-[#B45309]" />
                      </div>
                      <h2 id={`heading-${catSlug}`} className="text-xl font-bold text-gray-900">{cat.category}</h2>
                    </div>

                    <div className="space-y-3">
                      {cat.items.map((faq, idx) => {
                        const key = `${catSlug}-${idx}`;
                        const isOpen = openFaq === key;
                        return (
                          <article key={key} className="border border-gray-200 rounded-xl overflow-hidden hover:border-[#B45309]/40 transition-colors">
                            <button
                              type="button"
                              onClick={() => toggleFaq(key)}
                              className="w-full flex items-start justify-between px-6 py-5 text-left hover:bg-gray-50 transition-colors cursor-pointer gap-4"
                              aria-expanded={isOpen}
                            >
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-900 text-sm mb-1">
                                  <a id={`faq-${key}`}>{faq.q}</a>
                                </h4>
                                <p className="text-[#B45309] text-sm font-medium leading-snug">{faq.short}</p>
                              </div>
                              <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5 text-gray-400">
                                <Icon name={isOpen ? 'chevron-up' : 'chevron-down'} className="w-4 h-4" />
                              </span>
                            </button>
                            {isOpen && (
                              <div className="px-6 pb-5 border-t border-gray-100 pt-4 bg-gray-50/50">
                                <p className="text-gray-600 text-sm leading-relaxed">{faq.detail}</p>
                              </div>
                            )}
                          </article>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
            </div>

            {/* Sidebar */}
            <aside className="space-y-6">

              {/* Quick CTA */}
              <div className="bg-[#B45309] rounded-2xl p-6 text-white sticky top-28">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                  <Icon name="screwdriver-wrench" className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-bold text-lg mb-2">Still have questions?</h3>
                <p className="text-white/85 text-sm mb-5 leading-relaxed">
                  Call us directly — we&apos;re happy to talk through your project before you commit to anything.
                </p>
                <a
                  href="tel:+12082046331"
                  className="w-full inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-[#B45309] px-5 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer text-sm mb-3"
                >
                  <span className="w-4 h-4 flex items-center justify-center"><Icon name="phone" className="w-4 h-4" /></span>
                  (208) 204-6331
                </a>
                <a
                  href="#quote"
                  className="w-full inline-flex items-center justify-center gap-2 border border-white/50 hover:bg-white/10 text-white px-5 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  Request Free Estimate
                </a>
              </div>

              {/* Related Links */}
              <div className="bg-gray-50 rounded-2xl p-6">
                <h3 className="font-bold text-gray-900 mb-4 text-sm">Related Resources</h3>
                <ul className="space-y-3">
                  <li>
                    <Link to="/services" className="flex items-center gap-2 text-sm text-gray-700 hover:text-[#B45309] transition-colors cursor-pointer group">
                      <span className="w-5 h-5 flex items-center justify-center text-[#B45309]"><Icon name="arrow-right" className="w-3.5 h-3.5" /></span>
                      All Services Overview
                    </Link>
                  </li>
                  <li>
                    <Link to="/blog/handyman-services-cost-boise-idaho" className="flex items-center gap-2 text-sm text-gray-700 hover:text-[#B45309] transition-colors cursor-pointer">
                      <span className="w-5 h-5 flex items-center justify-center text-[#B45309]"><Icon name="arrow-right" className="w-3.5 h-3.5" /></span>
                      Handyman Cost Guide — Boise
                    </Link>
                  </li>
                  <li>
                    <Link to="/blog/handyman-vs-contractor-idaho" className="flex items-center gap-2 text-sm text-gray-700 hover:text-[#B45309] transition-colors cursor-pointer">
                      <span className="w-5 h-5 flex items-center justify-center text-[#B45309]"><Icon name="arrow-right" className="w-3.5 h-3.5" /></span>
                      Handyman vs. Contractor — Idaho
                    </Link>
                  </li>
                  <li>
                    <Link to="/blog/diy-vs-hiring-pro-idaho-homeowners" className="flex items-center gap-2 text-sm text-gray-700 hover:text-[#B45309] transition-colors cursor-pointer">
                      <span className="w-5 h-5 flex items-center justify-center text-[#B45309]"><Icon name="arrow-right" className="w-3.5 h-3.5" /></span>
                      DIY vs. Hiring a Pro — Idaho
                    </Link>
                  </li>
                  <li>
                    <Link to="/blog/how-to-vet-contractor-idaho" className="flex items-center gap-2 text-sm text-gray-700 hover:text-[#B45309] transition-colors cursor-pointer">
                      <span className="w-5 h-5 flex items-center justify-center text-[#B45309]"><Icon name="arrow-right" className="w-3.5 h-3.5" /></span>
                      How to Vet a Contractor — Idaho
                    </Link>
                  </li>
                  <li>
                    <Link to="/blog/licensed-insured-contractor-idaho" className="flex items-center gap-2 text-sm text-gray-700 hover:text-[#B45309] transition-colors cursor-pointer">
                      <span className="w-5 h-5 flex items-center justify-center text-[#B45309]"><Icon name="arrow-right" className="w-3.5 h-3.5" /></span>
                      Licensed &amp; Insured — Why It Matters
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Trust Badges */}
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <h3 className="font-bold text-gray-900 mb-4 text-sm">Why Fix Right Services</h3>
                <ul className="space-y-3">
                  {[
                    { icon: 'shield-halved', text: 'Licensed & Insured in Idaho' },
                    { icon: 'star', text: '5-Star Rated by Treasure Valley Homeowners' },
                    { icon: 'clock', text: 'Response within 24 hours' },
                    { icon: 'file-lines', text: 'Free written estimates' },
                    { icon: 'certificate', text: '30-Day Workmanship Warranty' },
                    { icon: 'location-dot', text: 'Locally owned & operated in Boise' },
                  ].map((item) => (
                    <li key={item.text} className="flex items-center gap-3 text-sm text-gray-700">
                      <span className="w-7 h-7 flex items-center justify-center bg-[#B45309]/10 rounded-lg flex-shrink-0">
                        <Icon name={item.icon} className="w-3.5 h-3.5 text-[#B45309]" />
                      </span>
                      {item.text}
                    </li>
                  ))}
                </ul>
              </div>

            </aside>
          </div>
        </div>
      </main>

      {/* When You Need a Specialist */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-amber-50 border-t border-amber-100" aria-labelledby="specialist-heading">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-start gap-4 mb-3">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
              <Icon name="triangle-exclamation" className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 id="specialist-heading" className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">When You Need a Specialist</h2>
              <p className="text-gray-600 max-w-2xl text-sm leading-relaxed">
                We&apos;re honest about what we do and what we don&apos;t. Some work requires a licensed specialist — not because we can&apos;t swing a wrench, but because Idaho law, safety codes, and your homeowner&apos;s insurance require it. Here&apos;s when to call someone else.
              </p>
            </div>
          </div>

          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {specialists.map((s) => (
              <div key={s.title} className="bg-white rounded-xl p-5 border border-amber-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name={s.icon} className="w-4 h-4 text-amber-600" />
                  </div>
                  <h3 className="font-bold text-gray-900 text-sm">{s.title}</h3>
                </div>
                <div className="mb-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">When to call them</p>
                  <p className="text-sm text-gray-700 leading-relaxed">{s.when}</p>
                </div>
                <div className="pt-3 border-t border-gray-100">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Why it matters</p>
                  <p className="text-xs text-gray-600 leading-relaxed">{s.why}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 bg-white border border-amber-200 rounded-xl p-5 flex items-start gap-4">
            <span className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0 mt-0.5">
              <Icon name="lightbulb" className="w-4 h-4 text-amber-600" />
            </span>
            <p className="text-sm text-gray-700 leading-relaxed">
              <strong>Not sure which category your job falls into?</strong> Call us at{' '}
              <a href="tel:+12082046331" className="text-[#B45309] font-semibold hover:underline cursor-pointer">(208) 204-6331</a>{' '}
              and describe what you need. We&apos;ll give you an honest answer — even if that means pointing you to someone else. We&apos;d rather refer you to the right person than take on work that isn&apos;t safe or legal for us to do.
            </p>
          </div>
        </div>
      </section>

      {/* Quote Form */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" id="quote" aria-label="Request a free estimate">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Ready to Book or Get a Quote?</h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Fill out the form and we&apos;ll get back to you within 24 hours. Prefer to talk? Call us directly at{' '}
                <a href="tel:+12082046331" className="text-[#B45309] font-semibold hover:underline cursor-pointer">(208) 204-6331</a>.
              </p>
              <div className="space-y-4">
                {[
                  { icon: 'clock', text: 'Most handyman jobs scheduled within 3–7 business days' },
                  { icon: 'tag', text: 'Free written estimates — no obligation' },
                  { icon: 'shield-halved', text: 'Licensed & insured in Idaho' },
                  { icon: 'certificate', text: '30-day workmanship warranty on all labor' },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-3">
                    <span className="w-9 h-9 flex items-center justify-center bg-[#B45309]/10 rounded-lg flex-shrink-0">
                      <Icon name={item.icon} className="w-4 h-4 text-[#B45309]" />
                    </span>
                    <p className="text-sm text-gray-700">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gray-50 rounded-2xl p-8 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-1">Request a Free Estimate</h3>
              <p className="text-gray-500 text-sm mb-6">We respond within 24 hours.</p>
              <QuoteForm />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img src="https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/2ea97a783d40b0afc97fadf5f7236f78.webp" alt="Fix Right Services" width="180" height="48" className="h-12 w-auto mb-4" loading="lazy" />
              <p className="text-gray-400 text-sm">Boise&apos;s trusted experts for fence installation, handyman services, and home remodels.</p>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Services</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/services/fence" className="hover:text-white transition-colors cursor-pointer">Fence Installation &amp; Repair</Link></li>
                <li><Link to="/services" className="hover:text-white transition-colors cursor-pointer">Handyman Services</Link></li>
                <li><Link to="/services" className="hover:text-white transition-colors cursor-pointer">Bathroom Remodels</Link></li>
                <li><Link to="/services" className="hover:text-white transition-colors cursor-pointer">Small Remodel Projects</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Resources</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Home</Link></li>
                <li><Link to="/services" className="text-gray-400 hover:text-white transition-colors cursor-pointer">All Services</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Tips &amp; Advice</Link></li>
                <li><Link to="/services/handyman-faq" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Handyman FAQ</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Contact</h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5"><Icon name="phone" className="w-3.5 h-3.5 text-[#F59E0B]" /></span>
                  <a href="tel:+12082046331" className="hover:text-white transition-colors cursor-pointer">(208) 204-6331</a>
                </li>
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5"><Icon name="envelope" className="w-3.5 h-3.5 text-[#F59E0B]" /></span>
                  <a href="mailto:Southernkole@gmail.com" className="hover:text-white transition-colors cursor-pointer">Southernkole@gmail.com</a>
                </li>
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5"><Icon name="location-dot" className="w-3.5 h-3.5 text-[#F59E0B]" /></span>
                  <span>Serving Boise, Meridian, Nampa &amp; Caldwell</span>
                </li>
              </ul>
              <div className="flex gap-3 mt-4">
                <a href="https://www.facebook.com/profile.php?id=61582658047880" aria-label="Facebook" rel="nofollow noopener noreferrer" target="_blank" className="w-10 h-10 bg-gray-800 hover:bg-[#B45309] rounded-lg flex items-center justify-center transition-colors cursor-pointer">
                  <Icon name="facebook" className="w-5 h-5" />
                </a>
                <a href="https://www.instagram.com/fixright_services/" aria-label="Instagram" rel="nofollow noopener noreferrer" target="_blank" className="w-10 h-10 bg-gray-800 hover:bg-[#B45309] rounded-lg flex items-center justify-center transition-colors cursor-pointer">
                  <Icon name="instagram" className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-gray-400">
            <p>&copy; 2025 Fix Right Services. All rights reserved.</p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Website Builder</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
